import * as React from 'react'
import NextLink from 'next/link'

import { ArrowForwardIos } from '@mui/icons-material'
import { Box, Divider, List, ListItem, ListItemButton, ListItemText, Stack } from '@mui/material'

import { getTarget } from '@/helpers/getTarget'
import { getLinkColorStyle } from '@/helpers/linkColors'
import { useHeaderContext } from '@/hooks/useHeaderContext'
import { commerceTheme as theme } from '@/themes/commerce/theme'

import type { NavigationNode } from '@/interfaces/NavigationNode'

interface MenuContentPanesProps {
  categories: NavigationNode[]
}

interface PanesState {
  hoveredItem: NavigationNode[] | null
  hoveredLink: NavigationNode[] | null
  itemSelected: string | null
  linkSelected: string | null
}

const getPaneTextProps = (node: NavigationNode) =>
  ({
    component: 'span',
    color: node.textColor ?? 'secondary',
    variant: 'body1',
    fontWeight: 'normal',
  }) as const

const CLOSED_PANES: PanesState = {
  hoveredItem: null,
  hoveredLink: null,
  itemSelected: null,
  linkSelected: null,
}

export const MenuContentPanes: React.FC<MenuContentPanesProps> = ({ categories }) => {
  const { headerAppBarRef, toggleIsMegaMenuOpen } = useHeaderContext()
  const [stateMenu, setStateMenu] = React.useState<PanesState>(CLOSED_PANES)

  const handleClose = () => {
    setStateMenu(CLOSED_PANES)
  }

  const [headerHeight, setHeaderHeight] = React.useState(0)

  React.useEffect(() => {
    if (headerAppBarRef?.current) {
      setHeaderHeight(headerAppBarRef.current.offsetHeight)
    }
  }, [headerAppBarRef])

  const Arrow = (
    <ArrowForwardIos
      sx={{
        stroke: `${theme.palette.border.default}`,
        strokeWidth: 1,
        height: 18,
        width: 18,
        marginTop: 1,
      }}
      color="secondary"
    />
  )

  return (
    <Box
      maxWidth={{ lg: 'xl', md: 'md' }}
      maxHeight={`calc(100vh - ${headerHeight}px)`}
      sx={{
        margin: 'auto',
        p: 3,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        maxHeight={`calc(100vh - ${headerHeight + 30}px)`}
        width="100%"
        sx={{ overflow: 'hidden' }}
        onMouseLeave={handleClose}
      >
        <List
          onMouseOver={() => setStateMenu((prev) => ({ ...prev, hoveredLink: null }))}
          sx={{ width: '29%', overflow: 'auto' }}
        >
          {categories.map((category, colIndex) => (
            <ListItem key={category.id} disablePadding secondaryAction={Arrow}>
              <ListItemButton
                component={NextLink}
                href={category.href ?? '#'}
                style={getLinkColorStyle(category)}
                selected={stateMenu.itemSelected === category.id}
                onMouseOver={() =>
                  setStateMenu((prev) => ({
                    ...prev,
                    itemSelected: category.id,
                    hoveredItem: category.children,
                  }))
                }
                onClick={toggleIsMegaMenuOpen(false, null, true)}
              >
                <ListItemText
                  sx={{ margin: 0 }}
                  primaryTypographyProps={getPaneTextProps(category)}
                  primary={category.title}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider orientation="vertical" variant="middle" flexItem />

        {stateMenu.hoveredItem && (
          <>
            <List sx={{ width: '29%', overflow: 'auto' }}>
              {stateMenu.hoveredItem.map((sub) => {
                return (
                  <ListItem key={sub.id} disablePadding secondaryAction={sub.children.length > 0 && Arrow}>
                    <ListItemButton
                      component={NextLink}
                      href={sub.href ?? '#'}
                      style={getLinkColorStyle(sub)}
                      onMouseOver={() =>
                        setStateMenu((prev) => ({
                          ...prev,
                          linkSelected: sub.id,
                          hoveredLink: sub.children,
                        }))
                      }
                      selected={stateMenu.linkSelected === sub.id}
                      onClick={toggleIsMegaMenuOpen(false, null, true)}
                    >
                      <ListItemText
                        primary={sub.title}
                        onClick={toggleIsMegaMenuOpen(false, null, true)}
                        sx={{ margin: 0 }}
                        primaryTypographyProps={getPaneTextProps(sub)}
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
            <Divider orientation="vertical" variant="middle" flexItem />
          </>
        )}
        {stateMenu.hoveredLink && (
          <List sx={{ width: '29%', overflow: 'auto' }}>
            {stateMenu.hoveredLink.map((link) => {
              return (
                <ListItem key={link.id} disablePadding>
                  <ListItemButton
                    component={NextLink}
                    href={link.href ?? '#'}
                    style={getLinkColorStyle(link)}
                    {...getTarget(link.openInNewTab)}
                    onClick={toggleIsMegaMenuOpen(false, null, true)}
                  >
                    <ListItemText
                      primary={link.title}
                      onClick={toggleIsMegaMenuOpen(false, null, true)}
                      sx={{ margin: 0 }}
                      primaryTypographyProps={getPaneTextProps(link)}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        )}
      </Stack>
    </Box>
  )
}
